import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ProgressSpinner } from "primereact/progressspinner";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import {
  Breadcrumb,
  DetailEmpty,
  DetailField,
  DetailGrid,
  FormSection,
  PageHeader,
  StatusBadge,
} from "../../components/ui";

import {
  useGetBenefitPartnerByIdQuery,
  useGetBenefitCategoriesQuery,
  useGetBenefitPartnerLocationsQuery,
} from "../../store/services/benefits";
import { useGetTenantsQuery } from "../../store/services/tenants";
import {
  getBenefitStatusLabel,
  getBenefitStatusTone,
} from "../../utils/constants/benefits-constants";
import { PartnerBenefitsListDT } from "../../components/DataTables/PartnerBenefitsListDT";

export const BenefitPartnerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: partner, isLoading } = useGetBenefitPartnerByIdQuery(
    id || "",
    { skip: !id }
  );
  // El listado no fuerza tenant: se busca la categoría/tenant del partner en
  // el universo global, igual que hace BenefitPartnerListDT.
  const { data: categories } = useGetBenefitCategoriesQuery({
    tenantId: null,
  });
  const { data: tenants } = useGetTenantsQuery({});
  const { data: locations } = useGetBenefitPartnerLocationsQuery(
    { partnerId: partner?.id || "", tenantId: partner?.tenantId },
    { skip: !partner?.id }
  );

  const categoryName = React.useMemo(() => {
    if (!partner) return undefined;
    return (categories ?? []).find((c) => c.id === partner.categoryId)?.name;
  }, [categories, partner]);

  const tenantName = React.useMemo(() => {
    if (!partner || !Array.isArray(tenants)) return undefined;
    const tenant = (tenants as any[]).find(
      (t) => String(t.id) === String(partner.tenantId)
    );
    return tenant?.provider;
  }, [tenants, partner]);

  if (isLoading) {
    return (
      <div className="gap-4 flex flex-column justify-content-center align-items-center p-3 min-h-11rem p-8">
        <h1 className="text-center text-3xl font-bold mb-6">
          Cargando contenido
        </h1>
        <ProgressSpinner />
      </div>
    );
  }

  if (!partner) {
    return (
      <div className="p-card p-5 text-center">
        <h2>Partner no encontrado</h2>
        <Button
          label="Volver"
          icon="pi pi-arrow-left"
          onClick={() => navigate("/benefits-partners")}
        />
      </div>
    );
  }

  const imageStyle = { objectFit: "cover" as const, borderRadius: 8, display: "block" };

  return (
    <>
      <Breadcrumb items={[{ label: "Partners", to: "/benefits-partners" }, { label: partner.name ?? "Detalle" }]} />
    <div className="p-card gap-4 p-3">
      {/* §6.1 — título centrado; "Editar" como ícono circular contiguo. */}
      <PageHeader
        title={partner.name ?? "-"}
        badge={
          <StatusBadge
            label={getBenefitStatusLabel(partner.status)}
            tone={getBenefitStatusTone(partner.status)}
          />
        }
        secondaryActions={[
          {
            icon: "pi pi-pencil",
            label: "Editar partner",
            filled: true,
            onClick: () => navigate(`/benefits-partners/${partner.id}/edit`),
          },
        ]}
      />

      <FormSection title="Información general" defaultOpen>
        <DetailGrid>
          <DetailField label="Categoría">
            {categoryName ?? partner.categoryId ?? <DetailEmpty />}
          </DetailField>
          <DetailField label="Tenant">{tenantName ?? partner.tenantId}</DetailField>
          <DetailField label="Slug">{partner.slug || <DetailEmpty />}</DetailField>
          <DetailField label="Destacado">
            {partner.featured ? (
              <Tag value="Destacado" severity="warning" icon="pi pi-star-fill" />
            ) : (
              <DetailEmpty>No</DetailEmpty>
            )}
          </DetailField>
          <DetailField label="Descripción">{partner.description || <DetailEmpty />}</DetailField>
        </DetailGrid>
      </FormSection>

      <FormSection title="Contacto" defaultOpen>
        <DetailGrid>
          <DetailField label="WhatsApp">{partner.whatsappNumber || <DetailEmpty />}</DetailField>
          <DetailField label="Instagram">
            {partner.instagramHandle ? `@${partner.instagramHandle}` : <DetailEmpty />}
          </DetailField>
          <DetailField label="Email de contacto">
            {partner.contactEmail || <DetailEmpty />}
          </DetailField>
        </DetailGrid>
      </FormSection>

      <FormSection title="Imágenes" defaultOpen>
        <DetailGrid>
          <DetailField label="Logo">
            {partner.logoUrl ? (
              <img
                src={partner.logoUrl}
                alt="Logo del partner"
                style={{ ...imageStyle, width: 120, height: 120 }}
              />
            ) : (
              <DetailEmpty>Sin logo cargado</DetailEmpty>
            )}
          </DetailField>
          <DetailField label="Portada">
            {partner.coverImageUrl ? (
              <img
                src={partner.coverImageUrl}
                alt="Portada del partner"
                style={{ ...imageStyle, width: "100%", maxWidth: 360, aspectRatio: "16 / 9" }}
              />
            ) : (
              <DetailEmpty>Sin portada cargada</DetailEmpty>
            )}
          </DetailField>
        </DetailGrid>
      </FormSection>

      <FormSection title={`Ubicaciones (${locations?.length ?? 0})`} defaultOpen>
        {locations?.length ? (
          <DetailGrid>
            {locations.map((location) => (
              <div key={location.id} className="p-3 border-1 border-round surface-border">
                <div className="font-medium mb-2">
                  {location.address || "Sin dirección"}
                </div>
                {location.phoneNumber && (
                  <div className="text-sm text-color-secondary mb-1">
                    <i className="pi pi-phone mr-2" />
                    {location.phoneNumber}
                  </div>
                )}
                {(location.latitude || location.longitude) && (
                  <div className="text-sm text-color-secondary">
                    <i className="pi pi-map-marker mr-2" />
                    {location.latitude}, {location.longitude}
                  </div>
                )}
              </div>
            ))}
          </DetailGrid>
        ) : (
          <DetailEmpty>No hay ubicaciones todavía</DetailEmpty>
        )}
      </FormSection>

      <FormSection title="Beneficios" defaultOpen>
        <PartnerBenefitsListDT
          partnerId={partner.id}
          tenantId={partner.tenantId}
        />
      </FormSection>
    </div>
    </>
  );
};
